import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';

/** Carte : la surface de base de l'application. */
export function Carte({ className, ...reste }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'rounded-[--radius-astro-lg] border border-bordure bg-surface shadow-astro-basse',
        className,
      )}
      {...reste}
    />
  );
}

export function EnTeteCarte({ className, ...reste }: ComponentProps<'div'>) {
  return <div className={cn('flex flex-col gap-1 p-5 pb-3', className)} {...reste} />;
}

export function TitreCarte({ className, ...reste }: ComponentProps<'h3'>) {
  return (
    <h3 className={cn('text-base font-semibold tracking-tight text-texte', className)} {...reste} />
  );
}

export function DescriptionCarte({ className, ...reste }: ComponentProps<'p'>) {
  return <p className={cn('text-sm text-texte-doux', className)} {...reste} />;
}

export function ContenuCarte({ className, ...reste }: ComponentProps<'div'>) {
  return <div className={cn('p-5 pt-0', className)} {...reste} />;
}

export function PiedCarte({ className, ...reste }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex items-center gap-2 border-t border-bordure px-5 py-3', className)}
      {...reste}
    />
  );
}

const variantesEtiquette = cva(
  cn(
    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5',
    'text-xs font-medium whitespace-nowrap',
  ),
  {
    variants: {
      ton: {
        neutre: 'border-bordure bg-surface-haute text-texte-doux',
        accent: 'border-accent/30 bg-accent-voile text-accent',
        /*
          Lavande de la famille Komett, PAS un second accent.

          La direction artistique reserve le teal aux elements interactifs ; ce ton
          sert uniquement a distinguer une categorie (type de client, modele
          prefourni). L'ancienne valeur (#7c5cff) etait un violet vif qui entrait en
          concurrence avec la couleur signature.
        */
        violet: 'border-violet/30 bg-violet-voile text-violet',
        succes: 'border-succes/30 bg-succes-voile text-succes',
        alerte: 'border-alerte/30 bg-alerte-voile text-alerte',
        danger: 'border-danger/30 bg-danger-voile text-danger',
      },
    },
    defaultVariants: { ton: 'neutre' },
  },
);

export interface ProprietesPastille
  extends ComponentProps<'span'>,
    VariantProps<typeof variantesEtiquette> {}

/** Pastille d'etat (statut de document, role, etc.). */
export function Pastille({ className, ton, ...reste }: ProprietesPastille) {
  return <span className={cn(variantesEtiquette({ ton }), className)} {...reste} />;
}

/**
 * Squelette de chargement.
 *
 * A DIMENSIONNER AUX MESURES FINALES par l'appelant. Un squelette plus petit que
 * le contenu qu'il remplace provoque un saut de mise en page a l'arrivee des
 * donnees — precisement le CLS que le §7 interdit.
 *
 * L'animation porte sur `opacity` seule ; `animate-pulse` de Tailwind convient.
 */
export function Squelette({ className, ...reste }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('animate-pulse rounded-[--radius-astro] bg-surface-haute', className)}
      aria-hidden
      {...reste}
    />
  );
}

/** Filet de separation. */
export function Separateur({
  className,
  orientation = 'horizontal',
  ...reste
}: ComponentProps<'div'> & { orientation?: 'horizontal' | 'vertical' }) {
  return (
    <div
      role="separator"
      aria-orientation={orientation}
      className={cn(
        'bg-bordure',
        orientation === 'horizontal' ? 'h-px w-full' : 'h-full w-px',
        className,
      )}
      {...reste}
    />
  );
}
